import { useEffect, useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { X, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

/**
 * Persistent side popup for judges.
 * Shows the latest manager message + 4 quick-action buttons (👍 👎 ❌ HELP).
 * Stays visible until the judge manually closes it.
 *
 * Notification rule: exactly 3 short beeps in quick succession on a NEW message id,
 * NEVER again until the next NEW message id arrives. Vibrate (if available) once.
 */
export default function JudgePopup({ message, onDismiss, onReact, onHelp }) {
  const { t } = useI18n();
  const [sending, setSending] = useState(null);
  const lastBeepedIdRef = useRef(null);

  useEffect(() => {
    if (!message?.id) return;
    if (lastBeepedIdRef.current === message.id) return; // already beeped for this msg
    lastBeepedIdRef.current = message.id;

    // Vibrate once (best-effort)
    try {
      if (navigator.vibrate) navigator.vibrate([120, 80, 120]);
    } catch { /* ignore */ }

    // Three short beeps in quick succession then silence.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const playAt = (offsetSec) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(880, ctx.currentTime + offsetSec);
        // Quick attack/release envelope to avoid clicks and stay short
        g.gain.setValueAtTime(0.0001, ctx.currentTime + offsetSec);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + offsetSec + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + offsetSec + 0.13);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + offsetSec);
        o.stop(ctx.currentTime + offsetSec + 0.14);
      };
      playAt(0);
      playAt(0.18);
      playAt(0.36);
      // Close context after all beeps done (avoid leak)
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 800);
    } catch { /* ignore */ }
  }, [message?.id]);

  if (!message) return null;

  const handleReact = async (type) => {
    setSending(type);
    try {
      await onReact(type, message?.id);
      toast.success(t("comms.reactionSent"));
      onDismiss?.();
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSending(null);
    }
  };

  const handleHelp = async () => {
    setSending("help");
    try {
      await onHelp();
      toast.success(t("comms.helpSent"));
    } catch {
      toast.error(t("common.error"));
    } finally {
      setSending(null);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        width: "min(94vw, 360px)",
        zIndex: 2147483000, // sit ABOVE timer-lock overlay (z=100) and everything else
      }}
      className="animate-in fade-in slide-in-from-right-4 duration-200"
      data-testid="judge-popup"
    >
      <div
        className="rounded-xl shadow-2xl border-2 border-primary/60 bg-card/95 backdrop-blur p-4 relative"
        style={{ boxShadow: "0 10px 40px rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.05)" }}
      >
        <button
          onClick={onDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted transition"
          aria-label="Close"
          data-testid="judge-popup-close"
        >
          <X size={18} />
        </button>
        <div className="text-xs uppercase tracking-wider text-primary font-bold mb-1">
          {t("comms.fromManager")}
        </div>
        <div
          className="text-foreground text-base leading-snug pr-6 mb-3 break-words"
          data-testid="judge-popup-message"
        >
          {message.message}
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleReact("thumb_up")}
            disabled={sending === "thumb_up"}
            className="text-2xl py-2"
            data-testid="judge-popup-thumb-up"
            title={t("comms.reactionOk")}
          >
            👍
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleReact("thumb_down")}
            disabled={sending === "thumb_down"}
            className="text-2xl py-2"
            data-testid="judge-popup-thumb-down"
            title={t("comms.reactionDown")}
          >
            👎
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleReact("cross")}
            disabled={sending === "cross"}
            className="text-2xl py-2"
            data-testid="judge-popup-cross"
            title={t("comms.reactionCross")}
          >
            ❌
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={handleHelp}
            disabled={sending === "help"}
            className="py-2 font-bold flex items-center justify-center gap-1"
            data-testid="judge-popup-help"
            title={t("comms.help")}
          >
            <LifeBuoy size={16} /> {t("comms.help")}
          </Button>
        </div>
      </div>
    </div>
  );
}
